import React, { useState } from 'react';
import classNames from 'classnames';

/* Validation */
import { Formik } from 'formik';
import validationSchema from './formConfig';

/* Gjensidige Core 20 Imports */
import utils from '@gjensidige/core-utilities-css';

/* Gjensidige Core 30 Imports */
import { Alert } from '@gjensidige/core-components/lib/alerts/default';
import { Col, Container, Row } from '@gjensidige/nci-core-grid';
import { Input } from '@gjensidige/core-components/lib/forms/input';
import { Dropdown } from '@gjensidige/core-components/lib/forms/dropdown';
import { InputGroupSimple } from '@gjensidige/core-components/lib/forms/input-group-simple';

/* API */
import axios from 'axios';
import { Endpoints } from '@internal/common';
import {Button} from "@gjensidige/core-components/lib/button";

const OfferCommercial = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    cvrNumber: '',
    subject: '',
    totalEmployees: '',
    hasInsurance: '',
    name: '',
    phoneNumber: '',
    email: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState(false);

  const sendFormData = () => {
    setApiError(false);
    axios
      .post(Endpoints.OfferCommercial, formData)
      .then(() => setSubmitted(true))
      .catch(() => setApiError(true));
  };

  const onDropdownChange = (value: string) => {
    setFormData({ ...formData, subject: value });
  };

  const dropDownOptions = [
    'Sundhedsforsikringer',
    'Erhvervsforsikringer',
    'Landbrugsforsikringer'
  ];

  return (
    <Container className={utils['mt-2']}>
      {submitted ? (
        <Row className={utils['justify-content-center']}>
          <Alert
            alertType="success"
            showIcon
            text="Tak, vi ringer til dig hurtigst muligt!​"
            className={utils['mb-3']}
          />
        </Row>
      ) : (
        <Formik
          enableReinitialize
          initialValues={{
            companyName: formData.companyName,
            cvrNumber: formData.cvrNumber,
            subject: formData.subject,
            totalEmployees: formData.totalEmployees,
            hasInsurance: formData.hasInsurance,
            name: formData.name,
            phoneNumber: formData.phoneNumber,
            email: formData.email
          }}
          validationSchema={validationSchema(formData)}
          onSubmit={() => {
            sendFormData();
          }}
        >
          {(props) => {
            const {
              handleSubmit,
              handleBlur,
              setFieldValue,
              touched,
              errors
            } = props;
            return (
              <form onSubmit={handleSubmit} className={utils['pb-3']}>
                <Row
                  className={classNames(
                    utils['mb-1'],
                    utils['justify-content-center']
                  )}
                ></Row>
                <Row className={utils['justify-content-center']}>
                  <Col lg={6}>
                    <Input
                      id="company-name"
                      labelText="Virksomhedens navn"
                      name="companyName"
                      className={utils['pb-2']}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setFieldValue('companyName', event.target.value);
                        setFormData({
                          ...formData,
                          companyName: event.target.value
                        });
                      }}
                      onBlur={handleBlur}
                      invalid={!!(touched.companyName && errors.companyName)}
                      errorText={errors.companyName}
                      value={formData.companyName}
                    />
                    <Input
                      id="cvr-number"
                      labelText="CVR-nummer"
                      name="cvrNumber"
                      className={utils['pb-2']}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setFieldValue('cvrNumber', event.target.value);
                        setFormData({
                          ...formData,
                          cvrNumber: event.target.value
                        });
                      }}
                      onBlur={handleBlur}
                      invalid={!!(touched.cvrNumber && errors.cvrNumber)}
                      errorText={errors.cvrNumber}
                      value={formData.cvrNumber}
                    />
                    <Dropdown
                      name="subject"
                      id="subject-dropdown"
                      labelText="Hvad vil du tale med os om?"
                      className={utils['pb-2']}
                      options={dropDownOptions.map((option) => {
                        return { labelText: option, value: option };
                      })}
                      value={formData.subject}
                      onChange={(value) => {
                        onDropdownChange(value);
                        setFieldValue('subject', value);
                      }}
                      onBlur={handleBlur}
                      invalid={!!(touched.subject && errors.subject)}
                      errorText={errors.subject}
                    />
                    {formData.subject === 'Sundhedsforsikringer' && (
                      <InputGroupSimple
                        vertical
                        header="Antal medarbejdere *"
                        id="total-empl-id"
                        className={utils['pb-2']}
                        name="totalEmployees"
                        centered={false}
                        onChange={(event) => {
                          setFormData({
                            ...formData,
                            totalEmployees: event.target.value
                          });
                          setFieldValue('totalEmployees', event.target.value);
                        }}
                        options={[
                          {
                            label: '1-4',
                            value: '1-4'
                          },
                          {
                            label: '5-99',
                            value: '5-99'
                          },
                          {
                            label: '+100',
                            value: '+100'
                          }
                        ]}
                        type="radio"
                        value={formData.totalEmployees}
                        onBlur={handleBlur}
                        invalid={
                          !!(touched.totalEmployees && errors.totalEmployees)
                        }
                        errorText={errors.totalEmployees}
                      />
                    )}
                    <InputGroupSimple
                      vertical
                      header="Har virksomheden allerede forsikringer i Gjensidige?"
                      id="has-insurance"
                      className={utils['pb-2']}
                      name="hasInsurance"
                      centered={false}
                      onChange={(event) => {
                        setFormData({
                          ...formData,
                          hasInsurance: event.target.value
                        });
                        setFieldValue('hasInsurance', event.target.value);
                      }}
                      options={[
                        {
                          label: 'Ja',
                          value: 'Ja'
                        },
                        {
                          label: 'Nej',
                          value: 'Nej'
                        }
                      ]}
                      type="radio"
                      value={formData.hasInsurance}
                      onBlur={handleBlur}
                      invalid={!!(touched.hasInsurance && errors.hasInsurance)}
                      errorText={errors.hasInsurance}
                    />
                    <Input
                      id="name"
                      labelText="Navn"
                      name="name"
                      className={utils['pb-2']}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setFieldValue('name', event.target.value);
                        setFormData({ ...formData, name: event.target.value });
                      }}
                      onBlur={handleBlur}
                      invalid={!!(touched.name && errors.name)}
                      errorText={errors.name}
                      value={formData.name}
                    />
                    <Input
                      id="phone-number"
                      labelText="Dit telefonnummer"
                      name="phoneNumber"
                      className={utils['pb-2']}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setFieldValue('phoneNumber', event.target.value);
                        setFormData({
                          ...formData,
                          phoneNumber: event.target.value
                        });
                      }}
                      onBlur={handleBlur}
                      invalid={!!(touched.phoneNumber && errors.phoneNumber)}
                      errorText={errors.phoneNumber}
                      value={formData.phoneNumber}
                    />
                    <Input
                      id="email"
                      labelText="Din mail"
                      name="email"
                      className={utils['pb-2']}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setFieldValue('email', event.target.value);
                        setFormData({
                          ...formData,
                          email: event.target.value
                        });
                      }}
                      onBlur={handleBlur}
                      invalid={!!(touched.email && errors.email)}
                      errorText={errors.email}
                      value={formData.email}
                    />
                    <Button variant={'primary'} type="submit" flexible>
                      Send
                    </Button>
                  </Col>
                </Row>
              </form>
            );
          }}
        </Formik>
      )}
      {apiError && (
        <Row className={utils['justify-content-center']}>
          <Alert
            alertType="error"
            showIcon
            text="Der skete en fejl ved indsendelse. Prøv venligst igen senere"
            title="Der skete en fejl."
            className={utils['mb-3']}
          />
        </Row>
      )}
    </Container>
  );
};

export default OfferCommercial;
